import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
} from "@angular/core";
import { GoogleMap } from "@angular/google-maps";

// ── Card Types (shared between dispatch-finder and dispatch-map) ──

export interface DriverCard {
  type: "driver";
  id: string;
  name: string;
  driverNo: string;
  driverType: string;
  pictureUrl: string | null;
  location: string | null;
  locationAge: string | null;
  latitude: number | null;
  longitude: number | null;
  milesLast7Days: number;
  homeCity: string | null;
  homeState: string | null;
  hoursOfService: number | null;
  assigned: boolean;
  selected: boolean;
  tractorId: string | null;
  trailerId: string | null;
  tractorNumber: string | null;
  trailerNumber: string | null;
  attachedTrailerNumbers: string[];
  schedulingTeamName: string | null;
  schedulingTeamBoardName: string | null;
  productQualifications: string[];
  prequalifiedProductQualifications: string[];
  roadCallStatus?: string | null;
  roadCallNumber?: string | null;
  roadCallOpenedDate?: Date | string | null;
}

export interface TrailerCard {
  type: "trailer";
  id: string;
  trailerNumber: string;
  equipmentCode: string | null;
  currentLocation: string | null;
  locationAge: string | null;
  latitude: number | null;
  longitude: number | null;
  terminal: string | null;
  inService: boolean;
  assigned: boolean;
  nextServiceDate: Date | null;
  daysUntilNextService: number | null;
  selected: boolean;
  attachedTractorNumber: string | null;
  roadCallStatus?: string | null;
  roadCallNumber?: string | null;
  roadCallOpenedDate?: Date | string | null;
}

export interface TractorCard {
  type: "tractor";
  id: string;
  tractorNumber: string;
  currentLocation: string | null;
  locationAge: string | null;
  latitude: number | null;
  longitude: number | null;
  terminal: string | null;
  inService: boolean;
  assigned: boolean;
  nextServiceDate: Date | null;
  daysUntilNextService: number | null;
  selected: boolean;
  attachedTrailerNumbers: string[];
  roadCallStatus?: string | null;
  roadCallNumber?: string | null;
  roadCallOpenedDate?: Date | string | null;
}

export type DispatchCard = DriverCard | TrailerCard | TractorCard;

export interface MarkerTooltipData {
  cardType: "driver" | "tractor" | "trailer" | "cluster";
  // driver
  name?: string;
  driverNo?: string;
  driverType?: string;
  schedulingTeamName?: string | null;
  schedulingTeamBoardName?: string | null;
  hoursOfService?: number | null;
  milesLast7Days?: number;
  // tractor / driver shared
  tractorNumber?: string | null;
  trailerNumber?: string | null;
  attachedTrailerNumbers?: string[];
  assignedDriverName?: string | null;
  // trailer
  equipmentCode?: string | null;
  attachedTractorNumber?: string | null;
  // shared
  location?: string | null;
  locationAge?: string | null;
  terminal?: string | null;
  inService?: boolean;
  productQualifications?: string[];
  // road call
  roadCallStatus?: string | null;
  roadCallNumber?: string | null;
  roadCallOpenedDate?: Date | string | null;
  // cluster
  clusterCount?: number;
  clusterDrivers?: string[];
  clusterTractors?: string[];
  clusterTrailers?: string[];
}

interface MapMarkerData {
  options: google.maps.MarkerOptions;
  cardIds: string[];
  isCluster: boolean;
  count: number;
  tooltipData: MarkerTooltipData;
}

// ── Component ──

@Component({
  selector: "app-dispatch-map",
  templateUrl: "./dispatch-map.component.html",
  styleUrls: ["./dispatch-map.component.css"],
})
export class DispatchMapComponent implements OnInit, OnChanges {
  @ViewChild(GoogleMap) map!: GoogleMap;

  // ── Inputs ──
  @Input() cards: DispatchCard[] = [];
  @Input() mapCenter: google.maps.LatLngLiteral = { lat: 39.8283, lng: -98.5795 };
  @Input() mapZoom = 4;
  @Input() mapOptions: google.maps.MapOptions = {
    mapTypeId: "roadmap",
    zoomControl: true,
    scrollwheel: true,
    disableDoubleClickZoom: false,
    maxZoom: 18,
    minZoom: 3,
  };
  @Input() showTypes: { drivers: boolean; trailers: boolean; tractors: boolean } = {
    drivers: true,
    trailers: true,
    tractors: true,
  };
  @Input() locationMarker: google.maps.MarkerOptions | null = null;
  @Input() locationCircle: google.maps.CircleOptions | null = null;

  // ── Outputs ──
  @Output() showTypesChange = new EventEmitter<{
    drivers: boolean;
    trailers: boolean;
    tractors: boolean;
  }>();
  @Output() markerClick = new EventEmitter<DispatchCard>();
  @Output() mapZoomChange = new EventEmitter<number>();

  // ── Internal map state ──
  allMapMarkers: MapMarkerData[] = [];
  hoveredMarker: MapMarkerData | null = null;
  tooltipX = 0;
  tooltipY = 0;

  ngOnInit(): void {
    // Initial render is handled by ngOnChanges via the cards input
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes["cards"] ||
      changes["showTypes"] ||
      changes["locationMarker"] ||
      changes["locationCircle"]
    ) {
      this.updateMapMarkers();
    }
  }

  // ── Map marker generation ──

  /** Round coordinate to 4 decimal places (~11m precision) for same-location matching */
  private posKey(lat: number, lng: number): string {
    return `${Math.round(lat * 10000)},${Math.round(lng * 10000)}`;
  }

  private updateMapMarkers(): void {
    if (typeof google === "undefined" || !google.maps) return;
    this.allMapMarkers = [];
    this.hoveredMarker = null;

    // ── Step 1: Collect ALL cards with valid positions (no showTypes filter yet) ──
    const cardsWithPos: { card: DispatchCard; lat: number; lng: number; posKey: string }[] = [];
    this.cards.forEach((card) => {
      if (card.latitude != null && card.longitude != null) {
        const lat = card.latitude;
        const lng = card.longitude;
        cardsWithPos.push({ card, lat, lng, posKey: this.posKey(lat, lng) });
      }
    });

    // ── Step 2: Build lookup maps ──
    const tractorCardById = new Map<string, TractorCard>();
    const trailerCardById = new Map<string, TrailerCard>();
    const trailerByNumber = new Map<string, TrailerCard>(); // trailerNumber -> TrailerCard
    const tractorDriverMap = new Map<string, DriverCard>();
    const trailerDriverMap = new Map<string, DriverCard>();
    (this.cards.filter((c) => c.type === "driver") as DriverCard[]).forEach((d) => {
      if (d.tractorId) tractorDriverMap.set(d.tractorId, d);
      if (d.trailerId) trailerDriverMap.set(d.trailerId, d);
    });
    (this.cards.filter((c) => c.type === "tractor") as TractorCard[]).forEach((t) => {
      tractorCardById.set(t.id, t);
    });
    (this.cards.filter((c) => c.type === "trailer") as TrailerCard[]).forEach((t) => {
      trailerCardById.set(t.id, t);
      trailerByNumber.set(t.trailerNumber, t);
    });

    // Index cardsWithPos by card.id for fast lookup
    const cardById = new Map<string, (typeof cardsWithPos)[0]>();
    cardsWithPos.forEach((item) => cardById.set(item.card.id, item));

    // ── Step 3: Group linked assets at same position ──
    // Type 1: driver + assigned tractor/trailer (by ID)
    // Type 2: tractor + attached trailers (by attachedTrailerNumbers matching trailerNumber)
    type LinkedGroup = {
      cards: DispatchCard[];
      primaryCard: DispatchCard;
      lat: number;
      lng: number;
      posKey: string;
    };
    const linkedGroups: LinkedGroup[] = [];
    const consumedIds = new Set<string>();

    // --- Pass A: Link drivers with their assigned tractor/trailer ---
    for (const item of cardsWithPos) {
      if (item.card.type !== "driver") continue;
      if (consumedIds.has(item.card.id)) continue;

      const driver = item.card as DriverCard;
      const groupCards: DispatchCard[] = [driver];
      const groupPosKey = item.posKey;

      if (driver.tractorId) {
        const tractorItem = cardById.get(driver.tractorId);
        if (
          tractorItem &&
          tractorItem.posKey === groupPosKey &&
          !consumedIds.has(tractorItem.card.id)
        ) {
          groupCards.push(tractorItem.card);
          consumedIds.add(tractorItem.card.id);
        }
      }

      if (driver.trailerId) {
        const trailerItem = cardById.get(driver.trailerId);
        if (
          trailerItem &&
          trailerItem.posKey === groupPosKey &&
          !consumedIds.has(trailerItem.card.id)
        ) {
          groupCards.push(trailerItem.card);
          consumedIds.add(trailerItem.card.id);
        }
      }

      if (groupCards.length > 1) {
        consumedIds.add(driver.id);
        linkedGroups.push({
          cards: groupCards,
          primaryCard: driver,
          lat: item.lat,
          lng: item.lng,
          posKey: groupPosKey,
        });
      }
    }

    // --- Pass B: Link tractors with attached trailers (by number) ---
    for (const item of cardsWithPos) {
      if (item.card.type !== "tractor") continue;
      if (consumedIds.has(item.card.id)) continue;

      const tractor = item.card as TractorCard;
      if (!tractor.attachedTrailerNumbers || tractor.attachedTrailerNumbers.length === 0) continue;

      const groupCards: DispatchCard[] = [tractor];
      const groupPosKey = item.posKey;

      for (const tn of tractor.attachedTrailerNumbers) {
        const trailerItem = cardById.get(trailerByNumber.get(tn)?.id ?? "");
        if (
          trailerItem &&
          trailerItem.posKey === groupPosKey &&
          !consumedIds.has(trailerItem.card.id)
        ) {
          groupCards.push(trailerItem.card);
          consumedIds.add(trailerItem.card.id);
        }
      }

      if (groupCards.length > 1) {
        consumedIds.add(tractor.id);
        linkedGroups.push({
          cards: groupCards,
          primaryCard: tractor,
          lat: item.lat,
          lng: item.lng,
          posKey: groupPosKey,
        });
      }
    }

    // ── Step 4: Build items array (linked groups + remaining individuals), filtered by showTypes ──
    // Use OR logic for linked groups: show if ANY of the card types in the group is visible
    type MapItem = {
      cards: DispatchCard[];
      primaryCard: DispatchCard;
      lat: number;
      lng: number;
      isLinkedGroup: boolean;
    };

    const allItems: MapItem[] = [];

    function shouldShowCard(card: DispatchCard): boolean {
      if (card.type === "driver") return this.showTypes.drivers;
      if (card.type === "tractor") return this.showTypes.tractors;
      if (card.type === "trailer") return this.showTypes.trailers;
      return true;
    }

    for (const group of linkedGroups) {
      // Show linked group if ANY of its cards pass the visibility check
      const visible = group.cards.some((c) => shouldShowCard.call(this, c));
      if (!visible) continue;

      allItems.push({
        cards: group.cards,
        primaryCard: group.primaryCard,
        lat: group.lat,
        lng: group.lng,
        isLinkedGroup: true,
      });
    }

    for (const item of cardsWithPos) {
      if (consumedIds.has(item.card.id)) continue;
      if (!shouldShowCard.call(this, item.card)) continue;

      allItems.push({
        cards: [item.card],
        primaryCard: item.card,
        lat: item.lat,
        lng: item.lng,
        isLinkedGroup: false,
      });
    }

    // ── Step 5: Grid-based clustering on all items ──
    const currentZoom = this.map?.getZoom() ?? this.mapZoom;
    const threshold = this.getClusterThreshold(currentZoom);
    const clusterMap = new Map<string, MapItem[]>();
    allItems.forEach((item) => {
      const gridKey = `${Math.floor(item.lat / threshold)},${Math.floor(item.lng / threshold)}`;
      if (!clusterMap.has(gridKey)) clusterMap.set(gridKey, []);
      clusterMap.get(gridKey)!.push(item);
    });

    // ── Step 6: Generate markers ──
    clusterMap.forEach((items) => {
      const count = items.reduce((s, i) => s + i.cards.length, 0);
      const centerLat = items.reduce((s, i) => s + i.lat, 0) / items.length;
      const centerLng = items.reduce((s, i) => s + i.lng, 0) / items.length;
      const position = { lat: centerLat, lng: centerLng };

      const drivers: DriverCard[] = [];
      const tractors: TractorCard[] = [];
      const trailers: TrailerCard[] = [];
      let hasSelected = false;

      for (const item of items) {
        for (const card of item.cards) {
          if (card.selected) hasSelected = true;
          if (card.type === "driver") drivers.push(card as DriverCard);
          else if (card.type === "tractor") tractors.push(card as TractorCard);
          else if (card.type === "trailer") trailers.push(card as TrailerCard);
        }
      }

      const allCardIds = items.flatMap((i) => i.cards.map((c) => c.id));
      const totalItems = items.length;

      if (totalItems > 1) {
        // ── Cluster marker ──
        const color = hasSelected ? "#7B1FA2" : "#37474F";
        const driverNames = drivers.map((d) => d.name);
        const tractorNumbers = tractors.map((t) => t.tractorNumber);
        const trailerNumbers = trailers.map((t) => t.trailerNumber);
        const lines: string[] = [];
        if (driverNames.length > 0) lines.push(`DRIVERS: ${driverNames.join(", ")}`);
        if (tractorNumbers.length > 0) lines.push(`TRACTORS: ${tractorNumbers.join(", ")}`);
        if (trailerNumbers.length > 0) lines.push(`TRAILERS: ${trailerNumbers.join(", ")}`);
        const title = lines.join("\n");

        const fontSize = count >= 100 ? "9" : count >= 10 ? "11" : "13";
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40"><circle cx="20" cy="20" r="18" fill="${color}" fill-opacity="0.9" stroke="white" stroke-width="2"/><text x="20" y="25" text-anchor="middle" fill="white" font-family="Arial,sans-serif" font-size="${fontSize}" font-weight="bold">${count}</text></svg>`;

        this.allMapMarkers.push({
          options: {
            position,
            title,
            icon: {
              url: `data:image/svg+xml,${encodeURIComponent(svg)}`,
              scaledSize: new google.maps.Size(40, 40),
              anchor: new google.maps.Point(20, 20),
            },
          },
          cardIds: allCardIds,
          isCluster: true,
          count,
          tooltipData: {
            cardType: "cluster",
            clusterCount: count,
            clusterDrivers: driverNames,
            clusterTractors: tractorNumbers,
            clusterTrailers: trailerNumbers,
          },
        });
      } else {
        // ── Single item marker ──
        const item = items[0];

        if (item.isLinkedGroup) {
          // ── Linked group marker (driver+tractor+trailer or tractor+trailer) ──
          const hasDriver = item.cards.some((c) => c.type === "driver");
          const linkedDriver = item.cards.find((c) => c.type === "driver") as
            | DriverCard
            | undefined;
          const linkedTractor = item.cards.find((c) => c.type === "tractor") as
            | TractorCard
            | undefined;
          const linkedTrailer = item.cards.find((c) => c.type === "trailer") as
            | TrailerCard
            | undefined;

          let title: string;
          let dotUrl: string;
          let tooltipData: MarkerTooltipData;

          if (hasDriver && linkedDriver) {
            // Driver-led group: use blue dot, show driver tooltip with equipment
            title = `${linkedDriver.name} (${linkedDriver.driverNo})`;
            dotUrl = hasSelected
              ? "https://maps.google.com/mapfiles/ms/icons/purple-dot.png"
              : "https://maps.google.com/mapfiles/ms/icons/blue-dot.png";
            tooltipData = {
              cardType: "driver",
              name: linkedDriver.name,
              driverNo: linkedDriver.driverNo,
              driverType: linkedDriver.driverType,
              schedulingTeamName: linkedDriver.schedulingTeamName,
              schedulingTeamBoardName: linkedDriver.schedulingTeamBoardName,
              location: linkedDriver.location,
              locationAge: linkedDriver.locationAge,
              hoursOfService: linkedDriver.hoursOfService,
              milesLast7Days: linkedDriver.milesLast7Days,
              tractorNumber: linkedTractor?.tractorNumber || linkedDriver.tractorNumber,
              trailerNumber: linkedTrailer?.trailerNumber || linkedDriver.trailerNumber,
              attachedTrailerNumbers:
                linkedTractor?.attachedTrailerNumbers || linkedDriver.attachedTrailerNumbers,
              roadCallStatus: linkedDriver.roadCallStatus ?? linkedTractor?.roadCallStatus ?? null,
              roadCallNumber: linkedDriver.roadCallNumber ?? linkedTractor?.roadCallNumber ?? null,
              roadCallOpenedDate:
                linkedDriver.roadCallOpenedDate ?? linkedTractor?.roadCallOpenedDate ?? null,
              productQualifications: linkedDriver.productQualifications,
            };
          } else if (linkedTractor) {
            // Tractor-led group (with attached trailers):
            // Show as tractor (red) if tractors are visible, otherwise as trailer (green)
            const showAsTractor = this.showTypes.tractors;
            if (showAsTractor) {
              title = `Tractor ${linkedTractor.tractorNumber}`;
              dotUrl = hasSelected
                ? "https://maps.google.com/mapfiles/ms/icons/purple-dot.png"
                : "https://maps.google.com/mapfiles/ms/icons/red-dot.png";
            } else {
              title = linkedTrailer ? `Trailer ${linkedTrailer.trailerNumber}` : `Trailer`;
              dotUrl = hasSelected
                ? "https://maps.google.com/mapfiles/ms/icons/purple-dot.png"
                : "https://maps.google.com/mapfiles/ms/icons/green-dot.png";
            }
            const assignedDriver = tractorDriverMap.get(linkedTractor.id);
            tooltipData = {
              cardType: showAsTractor ? "tractor" : "trailer",
              tractorNumber: linkedTractor.tractorNumber,
              trailerNumber: linkedTrailer?.trailerNumber,
              location: linkedTractor.currentLocation,
              locationAge: linkedTractor.locationAge,
              terminal: linkedTractor.terminal,
              inService: linkedTractor.inService,
              attachedTrailerNumbers: linkedTractor.attachedTrailerNumbers,
              assignedDriverName: assignedDriver ? assignedDriver.name : null,
              equipmentCode: linkedTrailer?.equipmentCode,
              roadCallStatus: linkedTractor.roadCallStatus ?? null,
              roadCallNumber: linkedTractor.roadCallNumber ?? null,
              roadCallOpenedDate: linkedTractor.roadCallOpenedDate ?? null,
            };
          } else {
            // Shouldn't reach here, but fallback
            title = "Linked";
            dotUrl = hasSelected
              ? "https://maps.google.com/mapfiles/ms/icons/purple-dot.png"
              : "https://maps.google.com/mapfiles/ms/icons/blue-dot.png";
            tooltipData = { cardType: "driver", name: "Unknown" };
          }

          this.allMapMarkers.push({
            options: {
              position,
              title,
              icon:
                tooltipData.roadCallStatus && !hasSelected ? this.roadCallIcon() : { url: dotUrl },
            },
            cardIds: allCardIds,
            isCluster: false,
            count: 1,
            tooltipData,
          });
        } else {
          // ── Individual card marker ──
          const card = item.primaryCard;
          let title = "";
          let dotUrl = "";

          let tooltipData: MarkerTooltipData;
          if (card.type === "driver") {
            const d = card as DriverCard;
            title = `${d.name} (${d.driverNo})`;
            dotUrl = hasSelected
              ? "https://maps.google.com/mapfiles/ms/icons/purple-dot.png"
              : "https://maps.google.com/mapfiles/ms/icons/blue-dot.png";
            tooltipData = {
              cardType: "driver",
              name: d.name,
              driverNo: d.driverNo,
              driverType: d.driverType,
              schedulingTeamName: d.schedulingTeamName,
              schedulingTeamBoardName: d.schedulingTeamBoardName,
              location: d.location,
              locationAge: d.locationAge,
              hoursOfService: d.hoursOfService,
              milesLast7Days: d.milesLast7Days,
              tractorNumber: d.tractorNumber,
              trailerNumber: d.trailerNumber,
              roadCallStatus: d.roadCallStatus ?? null,
              roadCallNumber: d.roadCallNumber ?? null,
              roadCallOpenedDate: d.roadCallOpenedDate ?? null,
              productQualifications: d.productQualifications,
            };
          } else if (card.type === "tractor") {
            const t = card as TractorCard;
            title = t.tractorNumber;
            dotUrl = hasSelected
              ? "https://maps.google.com/mapfiles/ms/icons/purple-dot.png"
              : "https://maps.google.com/mapfiles/ms/icons/red-dot.png";
            const assignedDriver = tractorDriverMap.get(t.id);
            tooltipData = {
              cardType: "tractor",
              tractorNumber: t.tractorNumber,
              location: t.currentLocation,
              locationAge: t.locationAge,
              terminal: t.terminal,
              inService: t.inService,
              attachedTrailerNumbers: t.attachedTrailerNumbers,
              assignedDriverName: assignedDriver ? assignedDriver.name : null,
              roadCallStatus: t.roadCallStatus ?? null,
              roadCallNumber: t.roadCallNumber ?? null,
              roadCallOpenedDate: t.roadCallOpenedDate ?? null,
            };
          } else {
            const t = card as TrailerCard;
            title = t.trailerNumber;
            dotUrl = hasSelected
              ? "https://maps.google.com/mapfiles/ms/icons/purple-dot.png"
              : "https://maps.google.com/mapfiles/ms/icons/green-dot.png";
            const assignedDriver = trailerDriverMap.get(t.id);
            tooltipData = {
              cardType: "trailer",
              trailerNumber: t.trailerNumber,
              equipmentCode: t.equipmentCode,
              location: t.currentLocation,
              locationAge: t.locationAge,
              terminal: t.terminal,
              inService: t.inService,
              attachedTractorNumber: t.attachedTractorNumber,
              assignedDriverName: assignedDriver ? assignedDriver.name : null,
              roadCallStatus: t.roadCallStatus ?? null,
              roadCallNumber: t.roadCallNumber ?? null,
              roadCallOpenedDate: t.roadCallOpenedDate ?? null,
            };
          }

          this.allMapMarkers.push({
            options: {
              position,
              title,
              icon:
                tooltipData.roadCallStatus && !hasSelected ? this.roadCallIcon() : { url: dotUrl },
            },
            cardIds: allCardIds,
            isCluster: false,
            count: 1,
            tooltipData,
          });
        }
      }
    });
  }

  private getClusterThreshold(zoom: number): number {
    const degreesPerPixel = 360 / (256 * Math.pow(2, Math.max(0, zoom)));
    const pixelSize = zoom >= 18 ? 1 : 50;
    return degreesPerPixel * pixelSize;
  }

  /**
   * Distinct amber marker used for units with an open road call. Built as an
   * inline SVG so it does not depend on Google's legacy dot image set.
   */
  private roadCallIcon(): google.maps.Icon {
    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" width="26" height="26">` +
      `<circle cx="13" cy="13" r="11" fill="#f59e0b" stroke="#ffffff" stroke-width="3"/>` +
      `<text x="13" y="18" text-anchor="middle" fill="#ffffff" font-family="Arial,sans-serif" font-size="14" font-weight="bold">!</text>` +
      `</svg>`;
    return {
      url: `data:image/svg+xml,${encodeURIComponent(svg)}`,
      scaledSize: new google.maps.Size(26, 26),
      anchor: new google.maps.Point(13, 13),
    };
  }

  /** True when at least one rendered marker represents a unit with an open road call. */
  get hasAnyRoadCall(): boolean {
    return this.allMapMarkers.some((m) => !!m.tooltipData.roadCallStatus);
  }

  // ── Event handlers ──

  onShowTypeChange(): void {
    this.showTypesChange.emit(this.showTypes);
    this.updateMapMarkers();
  }

  onMapZoomChanged(): void {
    this.mapZoomChange.emit(this.map?.getZoom() ?? this.mapZoom);
    this.updateMapMarkers();
  }

  onMapMouseMove(event: MouseEvent): void {
    this.tooltipX = event.clientX + 16;
    this.tooltipY = event.clientY + 16;
  }

  onMarkerMouseOver(markerData: MapMarkerData, event: google.maps.MapMouseEvent): void {
    this.hoveredMarker = markerData;
    if (event.domEvent instanceof MouseEvent) {
      this.tooltipX = event.domEvent.clientX + 16;
      this.tooltipY = event.domEvent.clientY + 16;
    }
  }

  onMarkerMouseOut(): void {
    this.hoveredMarker = null;
  }

  onMarkerClick(markerData: MapMarkerData): void {
    if (markerData.cardIds.length === 0) return;

    if (markerData.isCluster) {
      const gmap = this.map?.googleMap;
      const pos = markerData.options.position as google.maps.LatLngLiteral;
      const currentZoom = gmap?.getZoom() ?? this.mapZoom;
      const newZoom = Math.min(currentZoom + 3, 18);
      setTimeout(() => {
        this.mapCenter = { ...pos };
        this.mapZoom = newZoom;
        if (gmap) {
          gmap.setCenter(pos);
          gmap.setZoom(newZoom);
        }
      }, 0);
      return;
    }

    // Individual marker - emit the matching card
    const matchingCards = this.cards.filter((c) => markerData.cardIds.includes(c.id));
    if (matchingCards.length === 0) return;

    // Select highest-priority card: driver > tractor > trailer
    const card =
      matchingCards.find((c) => c.type === "driver") ||
      matchingCards.find((c) => c.type === "tractor") ||
      matchingCards[0];

    if (card) {
      this.markerClick.emit(card);
    }
  }
}
